import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material'

import { TwoFactorAuthType, type TwoFactorEnforcementState } from '@/api/types'

import { EmailMethodNotice } from './email-method-notice'

interface Props {
  label: string
  allowedMethods: TwoFactorAuthType[]
  value: TwoFactorAuthType
  onChange: (method: TwoFactorAuthType) => void
  // The user's current method; email shows as a notice unless the caller offers it as a choice.
  currentMethod?: TwoFactorAuthType | null
  currentEmail?: string | null
  enforcementDate?: string | null
  enforcementState?: TwoFactorEnforcementState
}

interface MethodOption {
  value: TwoFactorAuthType
  label: string
}

// Email is only offered while enforcement is off; then authenticator, the stronger method the company may require.
const METHOD_OPTIONS: MethodOption[] = [
  { value: TwoFactorAuthType.Email, label: 'Email' },
  { value: TwoFactorAuthType.Authenticator, label: 'Authenticator app' },
  { value: TwoFactorAuthType.Sms, label: 'Text message' },
  { value: TwoFactorAuthType.Call, label: 'Phone call' },
]

export function MfaMethodPicker({
  label,
  allowedMethods,
  value,
  onChange,
  currentMethod,
  currentEmail,
  enforcementDate,
  enforcementState,
}: Props) {
  const showEmailNotice = currentMethod === TwoFactorAuthType.Email && !allowedMethods.includes(TwoFactorAuthType.Email)

  return (
    <FormControl>
      {showEmailNotice && (
        <EmailMethodNotice email={currentEmail} enforcementDate={enforcementDate} enforcementState={enforcementState} />
      )}
      <FormLabel>{label}</FormLabel>
      <RadioGroup value={value} onChange={(e) => onChange(Number.parseInt(e.target.value) as TwoFactorAuthType)}>
        {/* Methods the user can't pick here are hidden; greyed-out options read as a company restriction. */}
        {METHOD_OPTIONS.filter((option) => allowedMethods.includes(option.value)).map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
            data-testid={`mfa-method-${option.value}`}
          />
        ))}
      </RadioGroup>
    </FormControl>
  )
}
